import type { Equipment, Mission, MissionStep } from '@/content/schema';
import type { Ctx } from '@/core/context';
import { tr } from '@/i18n';

/**
 * v1.3 共用小工具：
 * - 步驟要帶的東西（need ＋ needs）、哪裡拿得到
 * - 牆上溫度計的讀數（每天固定、每個地點不一樣）→ 3F 線邊溫度紀錄表
 * - 「🎮 作業在哪裡」：全場每一個作業小遊戲在哪一樓、哪一區
 */

/** 這一步要先有的背包物品（舊的 need ＋ v1.3 needs） */
export function stepNeeds(st: MissionStep): string[] {
  return [...new Set([...(st.need ? [st.need] : []), ...(st.needs ?? [])])];
}
/** 還沒拿到的（背包裡同一樣東西可以有好幾個，要一個一個算） */
export function missingNeeds(ctx: Ctx, st: MissionStep): string[] {
  const left = [...(ctx.day.pack ?? [])]; const out: string[] = [];
  for (const id of stepNeeds(st)) { const i = left.indexOf(id); if (i >= 0) left.splice(i, 1); else out.push(id); }
  return out;
}
/** 哪個貨架拿得到這個背包物品 */
export function packSources(ctx: Ctx, id: string): { floor: string; x: number; z: number; y: number; label: string; eq: string }[] {
  const out: { floor: string; x: number; z: number; y: number; label: string; eq: string }[] = [];
  for (const f of ctx.c.floors) for (const e of f.equipment) if (e.stock?.includes(id)) out.push({ floor: f.id, x: e.pos[0], z: e.pos[1], y: e.size[1] + 0.9, label: shortName(e.name), eq: e.id });
  return out;
}
export const shortName = (n: string) => n.split(tr("（"))[0].trim();
/** 「沙拉油（2F・配料線邊庫存架）」這種一行說明 */
export function needLines(ctx: Ctx, st: MissionStep): string[] {
  const have = new Set(ctx.day.pack ?? []); const miss = new Set(missingNeeds(ctx, st));
  return stepNeeds(st).map(id => {
    const it = ctx.c.packById.get(id); const src = packSources(ctx, id)[0];
    return miss.has(id) || !have.has(id)
      ? tr("⬜ {0} {1}：{2}", it?.icon ?? '📦', it?.name ?? id, src ? tr("{0}・{1}", src.floor, src.label) : tr("問問主管"))
      : tr("✅ {0} {1}", it?.icon ?? '📦', it?.name ?? id);
  });
}

// ------------------------------------------------------------------ 溫度計
type ThermoIt = { type: 'thermo'; spot: string; place: string; kind: 'room' | 'chill' | 'freeze'; limit: number };
const thermoOf = (e: Equipment): ThermoIt | null => (e.interaction?.type === 'thermo' ? e.interaction as ThermoIt : null);
/** 每天固定的讀數：大部分正常（比上限低 0.5～3.5°C），偶爾一個地點超標 */
export function thermoValue(day: number, spot: string, limit: number, kind: string): number {
  let h = 2166136261 ^ day; for (const ch of spot) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const r = ((h >>> 0) % 1000) / 1000; const r2 = (((h >>> 10) >>> 0) % 1000) / 1000;
  const bad = r2 < (kind === 'room' ? 0.12 : 0.18);
  const v = bad ? limit + 0.5 + r * 2.5 : limit - 0.5 - r * 3;
  return Math.round(v * 10) / 10;
}
/** 看溫度計：記到今天的筆記 */
export function readThermo(ctx: Ctx, e: Equipment): number | null {
  const it = thermoOf(e); if (!it) return null;
  const v = thermoValue(ctx.day.day, it.spot, it.limit, it.kind);
  (ctx.day.temps ??= {})[it.spot] = v;
  return v;
}
export function thermoText(e: Equipment, v: number): string {
  const it = thermoOf(e)!;
  const n = it.kind === 'room' ? tr("室溫") : it.kind === 'chill' ? tr("冷藏") : tr("冷凍");
  const bad = v > it.limit;
  return tr("🌡️ {0}：{1}°C（標準：{2} ≤ {3}°C）{4}", it.place, v, n, it.limit, bad ? tr("\n⚠️ 超過標準了！填紀錄表時要判「異常」，並寫處置。") : tr("\n✔ 正常。已經記在你的筆記裡，去紀錄表填寫。"));
}
/** 某層樓所有的溫度計（給 temp-log 小遊戲當參數） */
export function tempSpots(ctx: Ctx, floor = '3F'): { id: string; name: string; kind: 'room' | 'chill' | 'freeze'; limit: number; value: number; read: boolean; eq: string; x: number; z: number }[] {
  const f = ctx.c.floorById.get(floor as never); if (!f) return [];
  return f.equipment.filter(e => thermoOf(e)).map(e => {
    const it = thermoOf(e)!; const seen = ctx.day.temps?.[it.spot];
    return { id: it.spot, name: it.place, kind: it.kind, limit: it.limit, value: seen ?? thermoValue(ctx.day.day, it.spot, it.limit, it.kind), read: seen != null, eq: e.id, x: e.pos[0], z: e.pos[1] };
  });
}

// ------------------------------------------------------------------ 作業在哪裡
export interface JobSite { m: Mission; st: MissionStep; game: string; floor: string; target: string; zone: string; x: number; z: number; name: string }
export function jobSites(ctx: Ctx): JobSite[] {
  const out: JobSite[] = [];
  for (const m of ctx.c.missions) {
    const st = m.steps.find(s => s.do === 'minigame'); if (!st?.game) continue;
    const f = ctx.c.floorById.get(st.floor); const e = f?.equipment.find(q => q.id === st.target); if (!f || !e) continue;
    const zn = f.zones.find(z => e.pos[0] >= z.rect[0] && e.pos[0] <= z.rect[0] + z.rect[2] && e.pos[1] >= z.rect[1] && e.pos[1] <= z.rect[1] + z.rect[3] && !z.outdoor)
      ?? f.zones.find(z => e.pos[0] >= z.rect[0] && e.pos[0] <= z.rect[0] + z.rect[2] && e.pos[1] >= z.rect[1] && e.pos[1] <= z.rect[1] + z.rect[3]);
    out.push({ m, st, game: st.game, floor: st.floor, target: st.target, zone: zn ? shortName(zn.name) : '', x: e.pos[0], z: e.pos[1], name: shortName(e.name) });
  }
  const order = ['B1', '1F', '2F', '3F', '4F', '5F'];
  return out.sort((a, b) => order.indexOf(a.floor) - order.indexOf(b.floor) || a.zone.localeCompare(b.zone) || a.x - b.x);
}
